/**
 * Wspólny układ wszystkich tras z paskiem zaufania, nawigacją i skróconą stopką.
 */
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import {
  CalendarCheck2,
  Facebook,
  Instagram,
  Menu,
  Phone,
  X,
} from "lucide-react";
import { contact, physicalOffices, servicedCities } from "@/lib/site-data";
import { trackConversion } from "@/lib/analytics";
import { TrustBar } from "@/components/site/TrustBar";
import "./SiteLayout.css";

const nav = [
  { label: "Gwarancja", href: "/#gwarancja" },
  { label: "Test 2 min", href: "/#test" },
  { label: "Kancelarie", href: "/#kancelarie" },
  { label: "Jak działamy", href: "/#jak-to-dziala" },
  { label: "Opinie", href: "/#opinie" },
  { label: "Kontakt", href: "/#kontakt" },
];

const localNav = [
  { label: "Gwarancja", href: "#gwarancja" },
  { label: "Test 2 min", href: "#test" },
  { label: "Cena", href: "#cena" },
  { label: "Opinie", href: "#opinie" },
  { label: "Miasta", href: "/kancelarie" },
];

type LayoutProps = {
  children: React.ReactNode;
  title: string;
  description: string;
  minimal?: boolean;
  localNavigation?: boolean;
  contactNavigation?: boolean;
  localCitySlug?: string;
};

const brandLogos = {
  dark: {
    png: "/manus-storage/upadlosci-ekspert-logo-dark_8dc12533.png",
    webp: "/manus-storage/upadlosci-ekspert-logo-dark-216_5f48d8b7.webp 216w, /manus-storage/upadlosci-ekspert-logo-dark-432_6a1fe56f.webp 432w",
  },
  light: {
    png: "/manus-storage/upadlosci-ekspert-logo-light_6c156281.png",
    webp: "/manus-storage/upadlosci-ekspert-logo-light-216_7da5521a.webp 216w, /manus-storage/upadlosci-ekspert-logo-light-432_6dd93ecd.webp 432w",
  },
} as const;

const footerOfficeOrder = [
  "katowice",
  "zory",
  "lodz",
  "czestochowa",
  "bielsko-biala",
  "warszawa",
];

function Brand({
  campaign = false,
  tone = "dark",
}: {
  campaign?: boolean;
  tone?: keyof typeof brandLogos;
}) {
  const logo = brandLogos[tone];

  return (
    <Link
      href={campaign ? "/lp/komornik" : "/"}
      className="brand"
      aria-label="Upadłości Ekspert - strona główna"
    >
      <picture>
        <source
          type="image/webp"
          srcSet={logo.webp}
          sizes="(max-width: 699px) 132px, 216px"
        />
        <img
          src={logo.png}
          width="500"
          height="101"
          alt="Logo Upadłości Ekspert - Z nami odzyskasz spokój"
          className={`brand-logo brand-logo--${tone}`}
        />
      </picture>
    </Link>
  );
}

function TikTokIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
      <path d="M14.31 3c.3 2.42 1.66 4.03 4.69 4.23v3.04c-1.76.17-3.3-.4-4.64-1.2v5.69c0 7.23-7.89 9.49-11.05 4.31-2.03-3.33-.79-9.18 5.73-9.42v3.21c-.5.08-1.04.21-1.53.47-1.47.78-1.11 2.74-.46 3.44 1.25 1.35 3.72.91 4.17-.86.08-.31.05-5.55.05-8.46V3h3.04Z" />
    </svg>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div className="shell-wide footer-grid">
        <div>
          <Brand tone="light" />
          <p className="footer-statement">
            Upadłości Ekspert - pomagamy wyjść z długów przez upadłość
            konsumencką. Bezpłatna analiza, jasna cena, pisemna gwarancja.
          </p>
          <div className="footer-socials" aria-label="Media społecznościowe">
            <a
              className="footer-social-link footer-social-link--facebook"
              href="https://www.facebook.com/upadlosciexpert/"
              target="_blank"
              rel="noreferrer"
              aria-label="Upadłości Ekspert na Facebooku"
              title="Facebook"
            >
              <Facebook size={21} strokeWidth={2.3} />
            </a>
            <a
              className="footer-social-link footer-social-link--instagram"
              href="https://www.instagram.com/upadlosci_ekspert/"
              target="_blank"
              rel="noreferrer"
              aria-label="Upadłości Ekspert na Instagramie"
              title="Instagram"
            >
              <Instagram size={21} strokeWidth={2.3} />
            </a>
            <a
              className="footer-social-link footer-social-link--tiktok"
              href="https://www.tiktok.com/@upadlosckonsumencka_ep?lang=pl-PL"
              target="_blank"
              rel="noreferrer"
              aria-label="Upadłości Ekspert na TikToku"
              title="TikTok"
            >
              <TikTokIcon />
            </a>
          </div>
        </div>
        <div className="footer-contact-block">
          <p className="footer-label">Szybki kontakt</p>
          <a className="footer-contact-phone" href={contact.phoneHref}>
            {contact.phoneDisplay}
          </a>
          <a href="mailto:biuro@upadlosci-ekspert.pl">
            biuro@upadlosci-ekspert.pl
          </a>
          <span>{contact.hours}</span>
        </div>
        <div className="footer-documents-block">
          <p className="footer-label">Dokumenty</p>
          <Link href="/polityka-prywatnosci">Polityka prywatności</Link>
          <Link href="/regulamin">Regulamin</Link>
          <button
            className="cookie-link"
            type="button"
            onClick={() =>
              window.dispatchEvent(new Event("open-cookie-settings"))
            }
          >
            Ustawienia cookies
          </button>
          <Link href="/kontakt">Kontakt</Link>
        </div>
      </div>

      <div className="shell-wide footer-company-register-compact">
        <strong>Upadłości Ekspert to marka Expert Partner Sp. z o.o.</strong>
        <span>ul. Przemysłowa 10, 40-020 Katowice</span>
        <span>
          Sąd Rejonowy Katowice-Wschód w Katowicach, Wydział VIII Gospodarczy
          Krajowego Rejestru Sądowego
        </span>
        <span>KRS 0000573646 | NIP 9542757827 | REGON 362390928</span>
      </div>

      <section
        className="shell-wide footer-local-directory"
        aria-labelledby="footer-locations-title"
      >
        <div className="footer-local-heading">
          <div>
            <p className="footer-label">Pomoc lokalnie</p>
            <h2 id="footer-locations-title">Podstrony lokalizacyjne</h2>
          </div>
          <Link href="/kancelarie">Zobacz wszystkie miasta</Link>
        </div>
        <div className="footer-location-groups">
          <div>
            <p>Nasze kancelarie</p>
            <nav aria-label="Nasze kancelarie">
              {footerOfficeOrder.map(slug => {
                const office = physicalOffices.find(item => item.slug === slug);
                if (!office) return null;
                return (
                  <Link
                    key={office.slug}
                    href={`/upadlosc-konsumencka-${office.slug}`}
                  >
                    Upadłość konsumencka {office.city}
                  </Link>
                );
              })}
            </nav>
          </div>
          <div>
            <p>Obsługujemy całą Polskę</p>
            <nav aria-label="Miasta obsługiwane zdalnie">
              {servicedCities.map(office => (
                <Link
                  key={office.slug}
                  href={`/upadlosc-konsumencka-${office.slug}`}
                >
                  Upadłość konsumencka {office.city}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </section>
    </footer>
  );
}

export function SiteLayout({
  children,
  title,
  description,
  minimal = false,
  localNavigation = false,
  contactNavigation = false,
  localCitySlug,
}: LayoutProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showMobileBar, setShowMobileBar] = useState(false);
  const [pathname] = useLocation();
  const isSubpage = pathname !== "/";
  const activeNav = localNavigation ? localNav : nav;
  const conversationHref = localNavigation
    ? "#kontakt"
    : contactNavigation
      ? "#kontakt"
      : "/#kontakt";
  const mobileConversationHref = localNavigation
    ? "#kontakt"
    : contactNavigation
      ? "#kontakt"
      : "/kontakt#kontakt";

  useEffect(() => {
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", description);
  }, [title, description]);
  useEffect(() => {
    if (!window.location.hash) window.scrollTo({ top: 0, behavior: "auto" });
  }, [title]);
  useEffect(() => {
    setIsOpen(false);
  }, [title]);
  useEffect(() => {
    if (
      ![
        "/jak-to-dziala",
        "/cennik",
        "/polityka-prywatnosci",
        "/polityka-cookies",
      ].includes(window.location.pathname)
    )
      return;
    const root = document.querySelector(".site-shell main");
    if (!root) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
      node.nodeValue = node.nodeValue?.replaceAll("-", "-") ?? null;
      node = walker.nextNode();
    }
  }, []);
  useEffect(() => {
    const update = () => {
      const next = window.scrollY > 560;
      setShowMobileBar(current => (current === next ? current : next));
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, []);
  useEffect(() => {
    const onPhoneClick = (event: MouseEvent) => {
      const target = event.target as Element | null;
      const phoneLink = target?.closest<HTMLAnchorElement>('a[href^="tel:"]');
      if (!phoneLink) return;
      const location =
        phoneLink.dataset.location ??
        (phoneLink.closest(".local-office-hero")
          ? "hero"
          : phoneLink.closest(".mobile-subpage-cta")
            ? "sticky"
            : phoneLink.closest(".location-truth-card")
              ? "karta"
              : phoneLink.closest(".site-header")
                ? "header"
                : "site");
      trackConversion("contact_phone_click", { placement: location });
    };
    document.addEventListener("click", onPhoneClick);
    return () => document.removeEventListener("click", onPhoneClick);
  }, [localCitySlug]);
  useEffect(() => {
    const scrollToHash = (behavior: ScrollBehavior = "auto") => {
      const id = decodeURIComponent(window.location.hash.replace(/^#/, ""));
      if (!id) return;
      window.requestAnimationFrame(() => {
        const section = document.getElementById(id);
        const mobileGuarantee =
          id === "gwarancja" && window.matchMedia("(max-width: 699px)").matches;
        const contactChoices =
          id === "kontakt"
            ? section?.querySelector<HTMLElement>(
                ".contact-choice-grid, .contact-mode-panel"
              )
            : null;
        const mobileContactTitle =
          contactChoices && window.matchMedia("(max-width: 699px)").matches
            ? contactChoices.querySelector<HTMLElement>(".phone-choice strong")
            : null;
        const target =
          mobileContactTitle ??
          contactChoices ??
          (mobileGuarantee
            ? (section?.querySelector<HTMLElement>(".guarantee-copy") ??
              section)
            : section);
        const header = document.querySelector<HTMLElement>(".site-header");
        const trustBar = document.querySelector<HTMLElement>(".trust-topbar");
        if (!target) return;
        // Na telefonie dokument jest nad tekstem. Pozostawiamy jego końcówkę
        // widoczną, ale punktem przewinięcia jest już treść gwarancji.
        const documentPreviewOffset = mobileGuarantee ? 112 : 0;
        const top =
          target.getBoundingClientRect().top +
          window.scrollY -
          (header?.offsetHeight ?? 0) -
          (trustBar?.offsetHeight ?? 0) -
          documentPreviewOffset -
          (mobileContactTitle ? 6 : 18);
        window.scrollTo({ top: Math.max(0, top), behavior });
      });
    };
    const settleInitialHash = () => {
      const initialHash = window.location.hash;
      [0, 360].forEach(delay =>
        window.setTimeout(() => {
          if (window.location.hash === initialHash) scrollToHash();
        }, delay)
      );
    };
    const handleHashChange = () => scrollToHash();
    const handleAnchorClick = (event: MouseEvent) => {
      const anchor = (
        event.target as Element | null
      )?.closest<HTMLAnchorElement>('a[href*="#"]');
      if (
        !anchor ||
        anchor.target ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const targetUrl = new URL(anchor.href, window.location.href);
      if (
        targetUrl.origin !== window.location.origin ||
        targetUrl.pathname !== window.location.pathname ||
        !targetUrl.hash
      )
        return;
      if (!document.getElementById(decodeURIComponent(targetUrl.hash.slice(1))))
        return;
      event.preventDefault();
      window.history.pushState(
        null,
        "",
        `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`
      );
      scrollToHash("smooth");
    };
    settleInitialHash();
    window.addEventListener("hashchange", handleHashChange);
    document.addEventListener("click", handleAnchorClick);
    return () => {
      window.removeEventListener("hashchange", handleHashChange);
      document.removeEventListener("click", handleAnchorClick);
    };
  }, []);

  return (
    <div
      className={`site-shell${minimal ? " campaign-shell" : ""}${localNavigation ? " local-page-shell" : ""}`}
    >
      <TrustBar />
      {minimal ? (
        <header className="campaign-header">
          <Brand campaign />
          <a
            className="campaign-phone cta-attention cta-attention-phone"
            href={contact.phoneHref}
          >
            <Phone size={17} /> {contact.phoneDisplay}
          </a>
        </header>
      ) : (
        <header className="site-header">
          <div className="shell-wide header-inner">
            <Brand />
            <nav
              className="desktop-nav"
              aria-label={
                localNavigation
                  ? "Nawigacja strony lokalnej"
                  : "Główna nawigacja"
              }
            >
              {activeNav.map(item => (
                <a key={item.href} href={item.href}>
                  {item.label}
                </a>
              ))}
            </nav>
            <a
              className="header-booking cta-attention cta-attention-meeting"
              href={conversationHref}
            >
              Bezpłatna konsultacja
            </a>
            <a
              className="header-phone cta-attention cta-attention-phone"
              href={contact.phoneHref}
              aria-label={`Zadzwoń: ${contact.phoneDisplay}`}
            >
              <Phone size={16} />
              <span>{contact.phoneDisplay}</span>
            </a>
            <button
              className="menu-trigger"
              type="button"
              aria-label={isOpen ? "Zamknij menu" : "Otwórz menu"}
              aria-expanded={isOpen}
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? <X /> : <Menu />}
            </button>
          </div>
          {isOpen && (
            <nav
              className="mobile-nav"
              aria-label={
                localNavigation
                  ? "Nawigacja strony lokalnej"
                  : "Nawigacja mobilna"
              }
            >
              {activeNav.map(item => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setIsOpen(false)}
                >
                  {item.label}
                </a>
              ))}
              <a
                className="mobile-booking cta-attention cta-attention-meeting"
                href={conversationHref}
                onClick={() => setIsOpen(false)}
              >
                Bezpłatna konsultacja
              </a>
              <a
                className="mobile-nav-phone cta-attention cta-attention-phone"
                href={contact.phoneHref}
                onClick={() => setIsOpen(false)}
              >
                Zadzwoń: {contact.phoneDisplay}
              </a>
            </nav>
          )}
        </header>
      )}
      <main className={isSubpage ? "has-mobile-subpage-cta" : undefined}>
        {children}
      </main>
      <Footer />
      {!minimal && !isSubpage && (
        <a
          className={`mobile-cta ${showMobileBar ? "is-visible" : ""}`}
          href={contact.phoneHref}
          aria-label={`Zadzwoń: ${contact.phoneDisplay}`}
        >
          <Phone size={24} />
          <span>Zadzwoń: {contact.phoneDisplay}</span>
        </a>
      )}
      {!minimal && isSubpage && (
        <nav className="mobile-subpage-cta" aria-label="Szybki kontakt">
          <a href={contact.phoneHref}>
            <Phone size={18} /> Zadzwoń
          </a>
          <a href={mobileConversationHref}>
            <CalendarCheck2 size={18} /> Bezpłatna konsultacja
          </a>
        </nav>
      )}
    </div>
  );
}
